import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton, AppCheckbox } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import { prepService } from "../../../services/prepService";
import PrepRunDetailPanel from "../components/PrepRunDetailPanel";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import type { PrepJobResponse, PrepJobRunResponse } from "../../../types/prep";

function statusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "PARTIALLY_COMPLETED":
      return "info";
    case "RUNNING":
    case "PENDING":
      return "warning";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
}

function statusLabel(status: string | undefined, t: (key: string) => string): string {
  if (status === "PARTIALLY_COMPLETED") {
    return t("prep.runs.statusPartiallyCompleted");
  }
  return status ?? "—";
}

function isRunDetailAvailable(run: PrepJobRunResponse): boolean {
  return run.status !== "RUNNING" && run.status !== "PENDING";
}

function runProgress(run: PrepJobRunResponse, t: (key: string, options?: Record<string, unknown>) => string): {
  indeterminate: boolean;
  value: number;
  label: string;
} {
  const total = run.totalRecordCount ?? 0;
  const indexed = run.outputRowCount ?? 0;
  const failed = run.failedRecordCount ?? run.failedRecords?.length ?? 0;
  const processed = indexed + failed;
  if ((run.status === "RUNNING" || run.status === "PENDING") && processed === 0) {
    return {
      indeterminate: true,
      value: 0,
      label: total > 0 ? t("prep.runs.preparingRows", { count: total }) : t("prep.runs.preparing"),
    };
  }
  if (total <= 0) {
    const finished = run.status === "COMPLETED" || run.status === "PARTIALLY_COMPLETED" || run.status === "FAILED";
    return { indeterminate: false, value: finished ? 100 : 0, label: total > 0 ? `${processed}/${total}` : "—" };
  }
  return {
    indeterminate: false,
    value: Math.min(100, Math.round((processed / total) * 100)),
    label: `${processed}/${total}`,
  };
}

export default function PrepJobRunsPage() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<PrepJobResponse | null>(null);
  const [runs, setRuns] = useState<PrepJobRunResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [detailRun, setDetailRun] = useState<PrepJobRunResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const [jobRes, runsRes] = await Promise.all([prepService.get(id), prepService.listRuns(id)]);
    setJob(jobRes);
    setRuns(Array.isArray(runsRes) ? runsRes : []);
  }, [id]);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      load().catch(console.error);
    }, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const openRunDetail = async (run: PrepJobRunResponse) => {
    if (!isRunDetailAvailable(run)) return;
    setDetailRun(run);
    setLoadingDetail(true);
    try {
      const detail = await prepService.getRun(run.id);
      setDetailRun(detail);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("prep.runs.title")}
        subtitle={job?.name}
        actions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <AppCheckbox
              label={t("prep.runs.autoRefresh")}
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <AppButton variant="outlined" onClick={() => navigate(`/data-prep/${id}/design`)}>
              {t("prep.runs.backToDesign")}
            </AppButton>
            <AppButton variant="contained" onClick={() => load().catch(console.error)}>
              {t("prep.runs.refresh")}
            </AppButton>
          </Stack>
        }
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t("prep.runs.clickRowHint")}
      </Typography>
      <PageSectionCard noPadding>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("prep.runs.status")}</TableCell>
              <TableCell>{t("prep.runs.started")}</TableCell>
              <TableCell>{t("prep.runs.finished")}</TableCell>
              <TableCell>{t("prep.runs.progress")}</TableCell>
              <TableCell align="right">{t("prep.runs.outputRows")}</TableCell>
              <TableCell align="right">{t("prep.runs.failedRows")}</TableCell>
              <TableCell>{t("prep.runs.errors")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {t("prep.runs.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {runs.map((run) => {
              const failedCount = run.failedRecordCount ?? run.failedRecords?.length ?? 0;
              const clickable = isRunDetailAvailable(run);
              const progress = runProgress(run, t);
              return (
                <TableRow
                  key={run.id}
                  hover
                  onClick={() => clickable && openRunDetail(run)}
                  sx={custom.pages.dataPrep.prepJobRunsClickableRow(clickable)}
                >
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel(run.status, t)}
                      color={statusColor(run.status)}
                    />
                  </TableCell>
                  <TableCell>{run.startedAt ? new Date(run.startedAt).toLocaleString() : "—"}</TableCell>
                  <TableCell>{run.finishedAt ? new Date(run.finishedAt).toLocaleString() : "—"}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Stack spacing={0.5}>
                      <LinearProgress
                        variant={progress.indeterminate ? "indeterminate" : "determinate"}
                        value={progress.value}
                        color={run.status === "FAILED" ? "error" : "primary"}
                      />
                      <Box display="flex" justifyContent="space-between" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          {progress.indeterminate ? t("prep.runs.preparingShort") : `${progress.value}%`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {progress.label}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{run.outputRowCount ?? 0}</TableCell>
                  <TableCell align="right">{failedCount}</TableCell>
                  <TableCell sx={custom.pages.shared.tableCellWordBreak}>
                    {run.errors?.length ? run.errors[0] : "—"}
                    {(run.errors?.length ?? 0) > 1 ? ` (+${(run.errors?.length ?? 0) - 1})` : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </PageSectionCard>
      <PrepRunDetailPanel
        open={Boolean(detailRun)}
        run={detailRun}
        loading={loadingDetail}
        onClose={() => setDetailRun(null)}
      />
    </PageListContainer>
  );
}
